import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';
import CompanyDetail from './CompanyDetail';
import { stageLabel, STAGE_TONES, formatCurrency, formatDate, daysSince } from './format';

const PRIORITY_LABELS = { 1: 'Urgent', 2: 'High', 3: 'Normal', 4: 'Low', 5: 'Low' };

const CallListView = ({ meta }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    account_type: '',
    salesperson: '',
    state: '',
    last_order_after: '',
    last_order_before: '',
    min_revenue: '',
    include_contacted: false
  });
  const [pagination, setPagination] = useState({ total: 0, limit: 100, offset: 0 });

  const load = useCallback(
    (offset = 0) => {
      setLoading(true);
      const params = { limit: 100, offset };
      if (filters.account_type) params.account_type = filters.account_type;
      if (filters.salesperson) params.salesperson = filters.salesperson;
      if (filters.state) params.state = filters.state;
      if (filters.last_order_after) params.last_order_after = filters.last_order_after;
      if (filters.last_order_before) params.last_order_before = filters.last_order_before;
      if (filters.min_revenue) params.min_revenue = filters.min_revenue;
      if (filters.include_contacted) params.include_contacted = 'true';
      crmClient
        .callList(params)
        .then(payload => {
          setItems((payload && payload.items) || []);
          setPagination((payload && payload.pagination) || { total: 0, limit: 100, offset });
          setLoading(false);
        })
        .catch(() => {
          setError('Unable to load the call list.');
          setLoading(false);
        });
    },
    [filters]
  );

  useEffect(() => {
    load();
  }, [load]);

  const outcomes = (meta && meta.call_outcomes) || [];
  const salespeople = (meta && meta.salespeople) || [];
  const ownerLabel = auth0Id => {
    if (!auth0Id) return 'Unassigned';
    const match = salespeople.find(s => s.auth0_id === auth0Id);
    return match ? match.email : auth0Id;
  };
  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const openLogger = item => {
    setActiveItem(item);
    setOutcome('');
    setNotes('');
  };

  const submitOutcome = () => {
    if (!activeItem || !activeItem.crm_company_id || !outcome) {
      return;
    }
    setBusy(true);
    setError(null);
    crmClient
      .logCallOutcome(activeItem.crm_company_id, { outcome, notes, task_id: activeItem.crm_task_id })
      .then(() => {
        setBusy(false);
        setActiveItem(null);
        load();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to record outcome.');
      });
  };

  return (
    <div className={`crm-calllist${selectedId ? ' crm-companies--split' : ''}`}>
      <div className="crm-companies-main">
      <div className="crm-calllist-head">
        <div>
          <h2>Call List</h2>
          <p className="crm-muted">Merchants &amp; individuals needing a follow-up. Accounts drop off for 14 days after they're contacted.</p>
        </div>
        <button type="button" className="crm-btn crm-btn--ghost" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="crm-filters">
        <select value={filters.account_type} onChange={event => updateFilter('account_type', event.target.value)}>
          <option value="">All types</option>
          <option value="business">Business</option>
          <option value="individual">Individual</option>
        </select>
        <select value={filters.salesperson} onChange={event => updateFilter('salesperson', event.target.value)}>
          <option value="">All owners</option>
          <option value="unassigned">Unassigned</option>
          {salespeople.map(s => (
            <option key={s.auth0_id || s.email} value={s.auth0_id || ''}>
              {s.email}
            </option>
          ))}
        </select>
        <select value={filters.state} onChange={event => updateFilter('state', event.target.value)}>
          <option value="">All regions</option>
          {((meta && meta.states) || []).map(st => (
            <option key={st} value={st}>
              {st}
            </option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Min lifetime $"
          value={filters.min_revenue}
          onChange={event => updateFilter('min_revenue', event.target.value)}
          style={{ width: 130 }}
        />
        <label className="crm-date-filter">
          <span>Last order from</span>
          <input type="date" value={filters.last_order_after} onChange={event => updateFilter('last_order_after', event.target.value)} />
        </label>
        <label className="crm-date-filter">
          <span>to</span>
          <input type="date" value={filters.last_order_before} onChange={event => updateFilter('last_order_before', event.target.value)} />
        </label>
        <label className="crm-date-filter">
          <input type="checkbox" checked={filters.include_contacted} onChange={event => updateFilter('include_contacted', event.target.checked)} />
          <span>Include recently contacted</span>
        </label>
      </div>

      {error && <div className="crm-inline-error">{error}</div>}

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Priority</th>
              <th>Account</th>
              <th>Type</th>
              <th>Reason</th>
              <th>Phone</th>
              <th>Owner</th>
              <th className="crm-num">Last order</th>
              <th className="crm-num">Lifetime rev</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="crm-empty">
                  Loading call list…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="crm-empty">
                  No accounts currently need a call with these filters.
                </td>
              </tr>
            )}
            {!loading &&
              items.map(item => {
                const key = item.crm_task_id || `auto-${item.crm_company_id}`;
                const last = daysSince(item.last_order_date);
                return (
                  <tr
                    key={key}
                    className={`${item.crm_company_id ? 'crm-row-clickable' : ''}${selectedId === item.crm_company_id ? ' crm-row-active' : ''}`}
                    onClick={() => item.crm_company_id && setSelectedId(item.crm_company_id)}
                  >
                    <td>
                      <span className={`crm-priority crm-priority--${item.priority <= 2 ? 'high' : 'normal'}`}>
                        {PRIORITY_LABELS[item.priority] || 'Normal'}
                      </span>
                    </td>
                    <td>
                      <strong>{item.business_name || 'Task'}</strong>
                      {item.crm_stage && (
                        <span className={`crm-stage-pill crm-stage-pill--${STAGE_TONES[item.crm_stage] || 'slate'}`}>
                          {stageLabel(item.crm_stage)}
                        </span>
                      )}
                    </td>
                    <td>{item.account_type ? item.account_type.charAt(0).toUpperCase() + item.account_type.slice(1) : '—'}</td>
                    <td>{item.reason || '—'}</td>
                    <td>
                      {item.main_phone ? (
                        <a className="crm-phone-link" href={`tel:${item.main_phone}`}>{item.main_phone}</a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{ownerLabel(item.assigned_salesperson_auth0_id)}</td>
                    <td className="crm-num">
                      {formatDate(item.last_order_date)}
                      {last != null && <small className="crm-muted"> · {last}d</small>}
                    </td>
                    <td className="crm-num">{item.lifetime_revenue != null ? formatCurrency(item.lifetime_revenue) : '—'}</td>
                    <td className="crm-num">
                      {item.crm_company_id && (
                        <button type="button" className="crm-btn crm-btn--sm" onClick={event => { event.stopPropagation(); openLogger(item); }}>
                          Log call
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="crm-pagination">
        <span>
          {pagination.total
            ? `${pagination.offset + 1}–${Math.min(pagination.offset + pagination.limit, pagination.total)} of ${pagination.total.toLocaleString('en-US')}`
            : '0 accounts'}
        </span>
        <div>
          <button
            type="button"
            className="crm-btn crm-btn--ghost"
            disabled={pagination.offset <= 0}
            onClick={() => load(Math.max(pagination.offset - pagination.limit, 0))}
          >
            Previous
          </button>
          <button
            type="button"
            className="crm-btn crm-btn--ghost"
            disabled={pagination.offset + pagination.limit >= pagination.total}
            onClick={() => load(pagination.offset + pagination.limit)}
          >
            Next
          </button>
        </div>
      </div>
      </div>

      {selectedId && (
        <CompanyDetail companyId={selectedId} meta={meta} onClose={() => setSelectedId(null)} onChanged={() => load(pagination.offset)} />
      )}

      {activeItem && (
        <div className="crm-modal" role="dialog" aria-modal="true">
          <div className="crm-modal-card">
            <h3>Log call — {activeItem.business_name || 'Account'}</h3>
            <label>
              <span>Outcome</span>
              <select value={outcome} onChange={event => setOutcome(event.target.value)}>
                <option value="">Select…</option>
                {outcomes.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Notes</span>
              <textarea rows={3} value={notes} onChange={event => setNotes(event.target.value)} placeholder="Call notes…" />
            </label>
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn crm-btn--ghost" onClick={() => setActiveItem(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="crm-btn" onClick={submitOutcome} disabled={busy || !outcome}>
                Save outcome
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallListView;
